import type { CSSProperties, ReactElement } from 'react';
import { lockStateLabel, lockStateTitle } from './lockState';

/**
 * phase-tree-lock-open-shackle (Sharon, 2026-07-23/24) — the ONE lock-state
 * indicator, consumed by both the CTD tree and the centre-pane placement card.
 *
 * ## Why this exists
 *
 * phase-compiler-screen-batch item 5 rendered lock state as `🔒` / `🔓` emoji
 * at 9px, the unlocked one dimmed to 0.45 opacity. That fails three ways at
 * tree-row size, all reported by Sharon:
 *   1. The states are told apart mainly by OPACITY — a weight difference, which
 *      reads as "faint" ≈ "absent", not as a second state.
 *   2. An emoji at 9px is an illegible blob; the `🔓` glyph's shackle is only
 *      LIFTED, and a lifted shackle reads as closed when it is 9px tall.
 *   3. The same emoji-plus-opacity pattern was forked into the placement card,
 *      so the defect lived in two places.
 *
 * ## The fix
 *
 * A hand-authored SVG pair. The app has no icon library and V1 was emoji-only
 * (legible there only because a text label sat beside it), so no side-rotated
 * open padlock existed to reuse — it is authored here (see the phase report).
 *
 * The two glyphs are ONE OBJECT IN TWO STATES: identical body, identical stroke
 * weight, identical keyhole and optical alignment. They differ ONLY in the
 * shackle — the CLOSED shackle is a symmetric loop whose right leg enters the
 * body; the OPEN shackle is rotated about its left hinge so the free leg swings
 * CLEAR TO THE SIDE, leaving an unmistakable break in the loop that is obvious
 * without zooming. The states are NOT distinguished by colour, opacity or
 * weight — two shapes, not two weights.
 *
 * Display-only: it renders no interactive element. The CTD tree indicator must
 * not be clickable (Sharon's earlier ruling); the placement card supplies its
 * own <button> around this glyph and passes `decorative` so the button owns the
 * semantics.
 */

/**
 * Degrees the open shackle is rotated about its left hinge. Large enough that
 * the break is obvious at a 13–14px tree-row rendering — the whole point of the
 * phase. Tuned against the render-and-look crop.
 */
const OPEN_SHACKLE_ROTATION = 42;
const HINGE_X = 8;
const HINGE_Y = 10.5;

export interface LockStateIconProps {
  readonly locked: boolean;
  /** Pixel size of the square glyph. Tree ≈ 14; the card uses a touch larger. */
  readonly size?: number;
  /**
   * Tooltip. Defaults to naming the state AND pointing at where it is changed
   * (the placement card), because the tree indicator is display-only.
   */
  readonly title?: string;
  /** Accessible name. Defaults to the state vocabulary shared by both surfaces. */
  readonly label?: string;
  /**
   * When the glyph sits INSIDE a control that already labels it (the placement
   * card's toggle button), pass `decorative` so it is `aria-hidden` and adds no
   * second accessible name.
   */
  readonly decorative?: boolean;
  readonly testId?: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

export function LockStateIcon({
  locked,
  size = 14,
  title,
  label,
  decorative = false,
  testId,
  className,
  style,
}: LockStateIconProps): ReactElement {
  const resolvedTitle = title ?? lockStateTitle(locked);
  const resolvedLabel = label ?? lockStateLabel(locked);

  // Body + keyhole are IDENTICAL in both states — the object is the same lock.
  const body = (
    <>
      <rect x="4.5" y="10.5" width="15" height="10.5" rx="2.6" />
      {/* keyhole: a filled pin above a short stem */}
      <circle cx="12" cy="14.7" r="1.5" fill="currentColor" stroke="none" />
      <path d="M12 16.1 V18.2" />
    </>
  );

  // The ONLY difference between the two glyphs.
  const shackle = locked ? (
    // Symmetric closed loop: both legs land on the body top (y≈10.5).
    <path d="M8 10.5 V7 a4 4 0 0 1 8 0 V10.5" />
  ) : (
    // Open loop, rotated about the LEFT hinge so the free (right) leg swings
    // clear of the body and points up-and-out. The right leg deliberately does
    // NOT return to the body, so there is a visible gap on the body's top-right.
    <path
      d="M8 10.5 V7 a4 4 0 0 1 8 0"
      transform={`rotate(-${OPEN_SHACKLE_ROTATION} ${HINGE_X} ${HINGE_Y})`}
    />
  );

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-testid={testId}
      data-locked={locked ? 'true' : 'false'}
      className={className}
      style={{ display: 'block', overflow: 'visible', ...style }}
      // Display-only: role/label when standalone; hidden when a control wraps it.
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : resolvedLabel}
    >
      {!decorative && <title>{resolvedTitle}</title>}
      {shackle}
      {body}
    </svg>
  );
}
